/**
 * Route catalog for the navigate_to companion tool.
 *
 * Paths are verified against App.tsx. Tests assert literal paths so route
 * drift breaks the test (not silently the tool).
 */

export interface RouteCatalogEntry {
  id: string
  /** The full path pattern, with :param slots (e.g. "/backtester/editor/:id") */
  path: string
  /** Required parameter names in order they appear in the path */
  params?: string[]
  description: string
}

export const ROUTE_CATALOG: RouteCatalogEntry[] = [
  {
    id: 'dashboard',
    path: '/dashboard',
    description: 'Main dashboard workspace',
  },
  {
    id: 'history',
    path: '/history',
    description: 'Trade history and analysis',
  },
  {
    id: 'research',
    path: '/research/:tickerId',
    params: ['tickerId'],
    description: 'Research page for a specific ticker',
  },
  {
    id: 'exports',
    path: '/exports',
    description: 'Data exports',
  },
  {
    id: 'deep-research',
    path: '/deep-research',
    description: 'Deep Research AI assistant',
  },
  {
    id: 'backtest-strategies',
    path: '/backtester',
    description: 'Backtest strategies list',
  },
  {
    id: 'backtest-editor',
    path: '/backtester/editor/:id',
    params: ['id'],
    description: 'Backtest strategy editor',
  },
  {
    id: 'backtest-strategy',
    path: '/backtester/strategy/:id',
    params: ['id'],
    description: 'Backtest strategy variants and runs',
  },
  {
    id: 'backtest-stats',
    path: '/backtester/strategy/:id/stats',
    params: ['id'],
    description: 'Backtest strategy performance statistics',
  },
  {
    id: 'backtest-stats-workspace',
    path: '/backtester/strategy/:id/stats-v2',
    params: ['id'],
    description: 'Backtest strategy stats workspace with preset and custom layouts',
  },
  {
    id: 'backtest-run-report',
    path: '/backtester/strategy/:strategyId/run/:executionId',
    params: ['strategyId', 'executionId'],
    description: 'Run report for a single backtest execution',
  },
]

type RouteResult = { path: string } | { error: string }

/**
 * Resolve a route id + params to a concrete path.
 *
 * Returns `{ path }` on success or `{ error }` on:
 * - Unknown route id
 * - Missing required param
 * - Extraneous (unexpected) param
 *
 * Never navigates — callers decide what to do with the result.
 */
export function resolveRoute(routeId: string, params: Record<string, string> = {}): RouteResult {
  const entry = ROUTE_CATALOG.find((e) => e.id === routeId)
  if (!entry) {
    return { error: `Unknown route id: "${routeId}"` }
  }

  const expectedParams = entry.params ?? []

  // Check for missing params
  const missing = expectedParams.filter((p) => !(p in params))
  if (missing.length > 0) {
    return {
      error: `Missing required params for "${routeId}": ${missing.join(', ')}`,
    }
  }

  // Check for extraneous params (keys not in expectedParams)
  const extraneous = Object.keys(params).filter((p) => !expectedParams.includes(p))
  if (extraneous.length > 0) {
    return {
      error: `Extraneous params for "${routeId}": ${extraneous.join(', ')}`,
    }
  }

  // Substitute all :param slots
  let path = entry.path
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, encodeURIComponent(value))
  }

  return { path }
}

/**
 * Text block describing all navigable routes — injected into the companion
 * system prompt when navigate_to is in the active tool set.
 */
export function routeCatalogPromptText(): string {
  const lines = ['## available routes (for navigate_to)']
  for (const entry of ROUTE_CATALOG) {
    const paramList = entry.params ? ` (params: ${entry.params.join(', ')})` : ''
    lines.push(`- ${entry.id}: ${entry.path}${paramList} — ${entry.description}`)
  }
  return lines.join('\n')
}
